import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Collapse,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SyncIcon from '@mui/icons-material/Sync';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import type { MenuWithSite } from '@core/interfaces/api';
import { useSiteMealPlansQuery, useMenuQuery } from '../queries/menus.queries';
import {
  useDeleteSiteMenusMutation,
  useUpdateMenuMutation,
  useSyncAllMenusMutation,
  useSetDefaultMenuMutation,
  useSyncMenuMutation,
} from '../queries/menus.mutations';
import { createMenusApi, type DiningRdMenuSummary } from '../api/menus.api';
import { createMenuSlideApi } from '@features/digital-signage/menu-slide/api/menu-slide.api';
import { createSitesApi } from '@features/sites/api/sites.api';
import { auth } from '@core/utils/firebase/firebase';
import { formatDate } from '../utils/date.utils';
import { MenuSlidesView } from '../components/menu-slides-view';
import { MenuPdfDialog } from '../pdf/menu-pdf-dialog';

type Order = 'asc' | 'desc';

type ColumnKey = 'siteId' | 'name' | 'propertyId' | 'cycleStartDate' | 'updatedOn';

const columns: { id: ColumnKey | 'actions' | 'expand' | 'menuName' | 'diningRdMenuId'; label: string; sortable?: boolean }[] = [
  { id: 'expand', label: '' },
  { id: 'siteId', label: 'Site ID', sortable: true },
  { id: 'name', label: 'Site Name', sortable: true },
  { id: 'menuName', label: 'Menu' },
  { id: 'diningRdMenuId', label: 'Menu ID' },
  { id: 'propertyId', label: 'Property ID', sortable: true },
  { id: 'cycleStartDate', label: 'Cycle Start Date', sortable: true },
  { id: 'updatedOn', label: 'Last Sync', sortable: true },
  { id: 'actions', label: 'Actions' },
];

export interface MenuTableProps {
  organizationId?: number;
  siteName?: string;
  showSearch?: boolean;
  search?: string;
  onSearchChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

export function MenuTable({
  organizationId,
  siteName,
  showSearch = false,
  search: externalSearch,
  onSearchChange: externalOnSearchChange,
}: MenuTableProps) {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortField, setSortField] = useState<ColumnKey>('cycleStartDate');
  const [sortOrder, setSortOrder] = useState<Order>('desc');
  const [internalSearch, setInternalSearch] = useState('');
  const [menuToDelete, setMenuToDelete] = useState<MenuWithSite | null>(null);
  const [menuToEdit, setMenuToEdit] = useState<MenuWithSite | null>(null);
  const [editPropertyId, setEditPropertyId] = useState('');
  const [editCycleStartDate, setEditCycleStartDate] = useState('');
  const [editShowOnSignage, setEditShowOnSignage] = useState(false);
  const [editDisplayDuration, setEditDisplayDuration] = useState(15);
  const [editError, setEditError] = useState<string | null>(null);
  const [editAvailableMenus, setEditAvailableMenus] = useState<DiningRdMenuSummary[]>([]);
  const [editSelectedDiningRdMenuId, setEditSelectedDiningRdMenuId] = useState<number | ''>('');
  const [isLoadingEditMenus, setIsLoadingEditMenus] = useState(false);
  const [menuToSync, setMenuToSync] = useState<MenuWithSite | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const [expandedSiteId, setExpandedSiteId] = useState<number | null>(null);
  const [pdfMenu, setPdfMenu] = useState<MenuWithSite | null>(null);

  const search = externalSearch !== undefined ? externalSearch : internalSearch;

  useEffect(() => {
    setPage(0);
  }, [organizationId, siteName]);

  const {
    data,
    isLoading,
    isFetching,
    error,
  } = useSiteMealPlansQuery({
    organizationId,
    page,
    rowsPerPage,
    sortField,
    sortOrder,
    search: (showSearch || externalSearch !== undefined) ? search.trim() : undefined,
    siteName,
    onlyDefault: true,
    enabled: Boolean(organizationId),
  });

  const { mutateAsync: deleteSiteMenus, isPending: isDeleting } = useDeleteSiteMenusMutation();
  const { mutateAsync: updateMenu, isPending: isUpdating } = useUpdateMenuMutation();
  const { mutateAsync: syncAllMenus, isPending: isSyncing } = useSyncAllMenusMutation();
  const { mutateAsync: setDefaultMenu } = useSetDefaultMenuMutation();
  const { mutateAsync: syncMenu } = useSyncMenuMutation();

  const { data: expandedMenuData } = useMenuQuery({
    siteId: expandedSiteId,
    enabled: Boolean(expandedSiteId),
  });

  const rows = data?.data ?? [];
  const totalCount = data?.totalCount ?? 0;
  const isTableLoading = isLoading || isFetching;

  const handleRequestSort = (property: ColumnKey) => {
    const isAsc = sortField === property && sortOrder === 'asc';
    setSortField(property);
    setSortOrder(isAsc ? 'desc' : 'asc');
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (externalOnSearchChange) {
      externalOnSearchChange(event);
    } else {
      setInternalSearch(event.target.value);
    }
    setPage(0);
  };

  const handleDelete = (menu: MenuWithSite) => setMenuToDelete(menu);

  const confirmDelete = async () => {
    if (!menuToDelete) return;
    try {
      await deleteSiteMenus(menuToDelete.siteId);
      setMenuToDelete(null);
    } catch (err) {
      console.error('Failed to delete menus', err);
    }
  };

  const loadEditMenus = async (propertyId: string) => {
    const parsed = parseInt(propertyId, 10);
    if (Number.isNaN(parsed)) return;

    try {
      setIsLoadingEditMenus(true);
      setEditError(null);
      const menusApi = createMenusApi(auth as any);
      const menus = await menusApi.getAvailableMenus(parsed);
      setEditAvailableMenus(menus);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to load available menus.';
      setEditError(message);
    } finally {
      setIsLoadingEditMenus(false);
    }
  };

  const handleEdit = async (menu: MenuWithSite) => {
    setMenuToEdit(menu);
    setEditPropertyId(menu.propertyId ? String(menu.propertyId) : '');
    setEditCycleStartDate(menu.cycleStartDate || '');
    setEditError(null);
    setEditAvailableMenus([]);
    setEditSelectedDiningRdMenuId('');

    try {
      const sitesApi = createSitesApi(auth as any);
      const menuSlideApi = createMenuSlideApi(auth as any);

      const [site, menuSlide] = await Promise.all([
        sitesApi.getSite(menu.siteId),
        menuSlideApi.getMenuSlideBySiteId(menu.siteId),
      ]);

      setEditShowOnSignage(site.showMenu);
      setEditDisplayDuration(menuSlide?.displayDuration || 15);
    } catch (err) {
      console.error('Error fetching site and menu slide:', err);
      setEditShowOnSignage(false);
      setEditDisplayDuration(15);
    }

    // Auto-load available menus if we have a property ID
    if (menu.propertyId) {
      const menusApi = createMenusApi(auth as any);
      try {
        setIsLoadingEditMenus(true);
        const menus = await menusApi.getAvailableMenus(menu.propertyId);
        setEditAvailableMenus(menus);
        // Pre-select the current default for this site
        const defaultRow = data?.data?.find((r) => r.siteId === menu.siteId && r.isDefault);
        if (defaultRow?.diningRdMenuId) {
          setEditSelectedDiningRdMenuId(defaultRow.diningRdMenuId);
        }
      } catch {
        // Non-fatal — user can manually refresh
      } finally {
        setIsLoadingEditMenus(false);
      }
    }
  };

  const closeEditDialog = () => {
    setMenuToEdit(null);
    setEditError(null);
    setEditAvailableMenus([]);
    setEditSelectedDiningRdMenuId('');
  };

  const confirmEdit = async () => {
    if (!menuToEdit || !editPropertyId || !editCycleStartDate) {
      setEditError('Property ID and cycle start date are required.');
      return;
    }

    const parsedPropertyId = parseInt(editPropertyId, 10);
    if (Number.isNaN(parsedPropertyId)) {
      setEditError('Property ID must be a number.');
      return;
    }

    try {
      setEditError(null);

      await updateMenu({
        siteId: menuToEdit.siteId,
        propertyId: parsedPropertyId,
        cycleStartDate: editCycleStartDate,
      });

      if (editSelectedDiningRdMenuId) {
        // Sync the selected DiningRd menu to ensure it exists locally before setting as default
        await syncMenu({
          siteId: menuToEdit.siteId,
          propertyId: parsedPropertyId,
          cycleStartDate: editCycleStartDate,
          diningRdMenuId: Number(editSelectedDiningRdMenuId),
        });
        await setDefaultMenu({
          siteId: menuToEdit.siteId,
          diningRdMenuId: Number(editSelectedDiningRdMenuId),
        });
      }

      const sitesApi = createSitesApi(auth as any);
      const menuSlideApi = createMenuSlideApi(auth as any);

      await sitesApi.updateSite(menuToEdit.siteId, { showMenu: editShowOnSignage });

      if (editShowOnSignage && editDisplayDuration > 0) {
        try {
          const existingSlide = await menuSlideApi.getMenuSlideBySiteId(menuToEdit.siteId);
          if (existingSlide) {
            await menuSlideApi.updateMenuSlide(existingSlide.id, { displayDuration: editDisplayDuration });
          } else {
            await menuSlideApi.createMenuSlide({ siteId: menuToEdit.siteId, displayDuration: editDisplayDuration });
          }
        } catch (slideErr) {
          console.error('Error managing menu slide:', slideErr);
        }
      }

      closeEditDialog();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to update menu.';
      setEditError(message);
    }
  };

  const handleSync = (menu: MenuWithSite) => {
    setMenuToSync(menu);
    setSyncError(null);
  };

  const closeSyncDialog = () => {
    setMenuToSync(null);
    setSyncError(null);
  };

  const confirmSync = async () => {
    if (!menuToSync) return;

    if (!menuToSync.propertyId || !menuToSync.cycleStartDate) {
      setSyncError('This site is missing a property ID or cycle start date. Use Edit to configure it first.');
      return;
    }

    try {
      setSyncError(null);
      await syncAllMenus({
        siteId: menuToSync.siteId,
        propertyId: menuToSync.propertyId,
        cycleStartDate: menuToSync.cycleStartDate,
      });
      closeSyncDialog();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unable to sync menus.';
      setSyncError(message);
    }
  };

  const handleToggleExpand = (siteId: number) => {
    setExpandedSiteId(expandedSiteId === siteId ? null : siteId);
  };

  let emptyStateText = 'No sites available.';
  if (error) {
    emptyStateText = 'Unable to load menus.';
  } else if (showSearch && search.trim().length > 0) {
    emptyStateText = 'No sites match this search.';
  }

  if (!organizationId) {
    return (
      <Paper elevation={0} sx={{ p: 4 }}>
        <Typography align="center">Select a community to view menus.</Typography>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={{ p: 0 }}>
      <Box display="flex" flexDirection="column" gap={2}>
        {showSearch && (
          <TextField
            value={search}
            onChange={handleSearchChange}
            label="Search"
            variant="outlined"
            size="small"
            sx={{ maxWidth: 320 }}
          />
        )}
        <Box position="relative">
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {columns.map((column) => (
                    <TableCell key={column.id} sortDirection={sortField === column.id ? sortOrder : false}>
                      {column.sortable ? (
                        <TableSortLabel
                          active={sortField === column.id}
                          direction={sortField === column.id ? sortOrder : 'asc'}
                          onClick={() => handleRequestSort(column.id as ColumnKey)}
                        >
                          {column.label}
                        </TableSortLabel>
                      ) : (
                        column.label
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {!isTableLoading && rows.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={columns.length} align="center">
                      <Typography variant="body1" color="text.secondary">{emptyStateText}</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {rows.map((menu) => (
                  <React.Fragment key={menu.id}>
                    <TableRow hover>
                      <TableCell padding="checkbox">
                        <IconButton
                          size="small"
                          onClick={() => handleToggleExpand(menu.siteId)}
                          aria-expanded={expandedSiteId === menu.siteId}
                        >
                          {expandedSiteId === menu.siteId ? (
                            <ExpandLessIcon fontSize="small" />
                          ) : (
                            <ExpandMoreIcon fontSize="small" />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell>{menu.siteId}</TableCell>
                      <TableCell>{menu.siteName || menu.name}</TableCell>
                      <TableCell>
                        <Typography variant="body2">{menu.menuName || '—'}</Typography>
                      </TableCell>
                      <TableCell>{menu.diningRdMenuId ?? '—'}</TableCell>
                      <TableCell>{menu.propertyId ?? '—'}</TableCell>
                      <TableCell>{menu.cycleStartDate ? formatDate(menu.cycleStartDate) : 'N/A'}</TableCell>
                      <TableCell>{menu.updatedOn ? formatDate(new Date(menu.updatedOn).toISOString().split('T')[0]) : 'N/A'}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          <Tooltip title="Sync all menus">
                            <span>
                              <IconButton color="primary" onClick={() => handleSync(menu)} size="small">
                                <SyncIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Export Menu">
                            <span>
                              <IconButton color="secondary" onClick={() => setPdfMenu(menu)} size="small">
                                <PictureAsPdfIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Edit Menu">
                            <span>
                              <IconButton color="primary" onClick={() => handleEdit(menu)} size="small">
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Delete Menu">
                            <span>
                              <IconButton color="error" onClick={() => handleDelete(menu)} size="small">
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={columns.length} sx={{ py: 0 }}>
                        <Collapse in={expandedSiteId === menu.siteId} timeout="auto">
                          <Box sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                            {expandedMenuData && expandedMenuData.days && expandedMenuData.days.length > 0 ? (
                              <MenuSlidesView
                                days={expandedMenuData.days}
                                cycleStartDate={expandedMenuData.cycleStartDate}
                                daysInMenu={expandedMenuData.daysInMenu}
                              />
                            ) : (
                              <Typography variant="body2" color="text.secondary">
                                No menu data available for this menu.
                              </Typography>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {isTableLoading && (
            <Box
              position="absolute"
              top={0} left={0} right={0} bottom={0}
              display="flex" alignItems="center" justifyContent="center"
              bgcolor="rgba(255, 255, 255, 0.6)"
              zIndex={1}
            >
              <CircularProgress size={32} />
            </Box>
          )}
        </Box>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 20, 30, 50]}
        />
      </Box>

      {/* Delete dialog */}
      <Dialog open={Boolean(menuToDelete)} onClose={() => setMenuToDelete(null)}>
        <DialogTitle>Delete menu?</DialogTitle>
        <DialogContent>
          <Typography>
            This will remove the menu from gigi. It will not affect the actual dining menu.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMenuToDelete(null)}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" disabled={isDeleting}>
            {isDeleting ? 'Deleting…' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={Boolean(menuToEdit)} onClose={closeEditDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Menu</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              label="Property ID"
              type="number"
              value={editPropertyId}
              onChange={(e) => {
                setEditPropertyId(e.target.value);
                setEditAvailableMenus([]);
                setEditSelectedDiningRdMenuId('');
              }}
              fullWidth
              required
            />
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <FormControl fullWidth disabled={!editPropertyId || isLoadingEditMenus}>
                <InputLabel id="default-menu-label">Default Menu</InputLabel>
                <Select
                  labelId="default-menu-label"
                  value={editSelectedDiningRdMenuId}
                  label="Default Menu"
                  onChange={(e) => setEditSelectedDiningRdMenuId(e.target.value === '' ? '' : Number(e.target.value))}
                >
                  {editAvailableMenus.length === 0 ? (
                    <MenuItem disabled value="">
                      {isLoadingEditMenus ? 'Loading…' : 'No menus loaded'}
                    </MenuItem>
                  ) : (
                    editAvailableMenus.map((m) => (
                      <MenuItem key={m.MenuId} value={m.MenuId}>
                        {m.MenuId} - {m.MenuName} ({m.Season}, {m.DaysInMenu} days)
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
              <Tooltip title="Refresh menus">
                <span>
                  <IconButton
                    onClick={() => loadEditMenus(editPropertyId)}
                    disabled={!editPropertyId || isLoadingEditMenus}
                    sx={{ mt: 1 }}
                  >
                    {isLoadingEditMenus ? <CircularProgress size={20} /> : <RefreshIcon />}
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
            <TextField
              label="Cycle Start Date"
              type="date"
              value={editCycleStartDate}
              onChange={(e) => setEditCycleStartDate(e.target.value)}
              fullWidth
              required
              slotProps={{ inputLabel: { shrink: true } }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={editShowOnSignage}
                  onChange={(e) => setEditShowOnSignage(e.target.checked)}
                />
              }
              label="Display on Digital Signage"
            />
            {editShowOnSignage && (
              <TextField
                label="Display Duration (seconds)"
                type="number"
                value={editDisplayDuration}
                onChange={(e) => setEditDisplayDuration(Math.max(1, parseInt(e.target.value) || 15))}
                fullWidth
                required
                inputProps={{ min: 1 }}
              />
            )}
            {editError && <Alert severity="error">{editError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditDialog}>Cancel</Button>
          <Button
            onClick={confirmEdit}
            color="primary"
            disabled={isUpdating || !editPropertyId || !editCycleStartDate || (editShowOnSignage && editDisplayDuration < 1)}
          >
            {isUpdating ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Sync confirm dialog */}
      <Dialog open={Boolean(menuToSync)} onClose={closeSyncDialog}>
        <DialogTitle>Sync all menus?</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            <Typography>
              This will re-sync all DiningRd menus for <strong>{menuToSync?.siteName || menuToSync?.name}</strong> using the latest data from DiningRd.
            </Typography>
            {syncError && <Alert severity="error">{syncError}</Alert>}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeSyncDialog}>Cancel</Button>
          <Button onClick={confirmSync} color="primary" variant="contained" disabled={isSyncing}>
            {isSyncing ? 'Syncing…' : 'Sync'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* PDF dialog */}
      {pdfMenu && (
        <MenuPdfDialog
          open={Boolean(pdfMenu)}
          onClose={() => setPdfMenu(null)}
          siteId={pdfMenu.siteId}
          siteName={pdfMenu.siteName || pdfMenu.name}
        />
      )}
    </Paper>
  );
}
